import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, DownloadSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";

const PILL = {
  "A+": { background: "#dcfce7", color: "#15803d" },
  A: { background: "#dbeafe", color: "#1d4ed8" },
  "B+": { background: "#fef3c7", color: "#b45309" },
  B: { background: "#ffedd5", color: "#c2410c" },
  C: { background: "#fee2e2", color: "#b91c1c" },
  D: { background: "#fee2e2", color: "#991b1b" },
};

const REMARKS = {
  "A+": "An outstanding term — consistently excellent marks across subjects, strong class participation and exemplary conduct. A role-model performance; keep it up!",
  A: "A very strong term with consistently high marks across the board. Great focus and steady effort throughout.",
  "B+": "A good term overall. Solid understanding in most subjects; focused revision in the weaker areas will lift results further.",
  B: "A satisfactory term. Shows steady effort, but consistent daily practice is needed to reach the next band.",
  C: "A fair term. Needs regular study habits and closer attention in class — a parent-teacher meeting is recommended.",
  D: "This term's results are below expectations. Please arrange a meeting with the class teacher to plan remedial support.",
};

let html2pdfLoader = null;
function loadHtml2Pdf() {
  if (window.html2pdf) return Promise.resolve();
  if (!html2pdfLoader) {
    html2pdfLoader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return html2pdfLoader;
}

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

export default function ReportCard({ studentId, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [term, setTerm] = useState(null);
  const [remark, setRemark] = useState("");
  const [remarkDirty, setRemarkDirty] = useState(false);
  const [busy, setBusy] = useState(null);
  const sheetRef = useRef(null);

  const load = useCallback(
    async (t) => {
      setBusy("load");
      try {
        const r = await api.get(`/school/students/${studentId}/report-card`, { params: t ? { term: t } : {} });
        setData(r.data);
        setTerm(r.data.term);
        setRemark(r.data.remark || "");
        setRemarkDirty(false);
      } catch (e) {
        toast.error(formatErr(e));
        onClose();
      } finally {
        setBusy(null);
      }
    },
    [studentId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (studentId) {
      setData(null);
      load(null);
    }
  }, [studentId, load]);

  const download = async () => {
    setBusy("pdf");
    try {
      await loadHtml2Pdf();
      await window
        .html2pdf()
        .set({
          margin: 0.3,
          filename: `ReportCard_${data.student_name.replace(/\s+/g, "_")}_${data.term.replace(/\s+/g, "")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#f8fafc" },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(sheetRef.current)
        .save();
      toast.success(t("reportCard.pdfDownloaded", { defaultValue: "Report card PDF downloaded" }));
    } catch {
      toast.error(t("reportCard.pdfFailed", { defaultValue: "PDF export failed — please try again" }));
    } finally {
      setBusy(null);
    }
  };

  const saveRemark = async () => {
    setBusy("remark");
    try {
      await api.put(`/school/students/${studentId}/report-card/remarks`, { term: data.term, remark });
      toast.success(t("reportCard.remarkSaved", { defaultValue: "Remark saved" }));
      setRemarkDirty(false);
      setData({ ...data, remark });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      if (remarkDirty) {
        await api.put(`/school/students/${studentId}/report-card/remarks`, { term: data.term, remark });
        setRemarkDirty(false);
      }
      const r = await api.post(`/school/students/${studentId}/report-card/publish`, { term: data.term });
      toast.success(t("reportCard.publishedSuccess", { term: data.term, defaultValue: `${data.term} report card published to the parent portal` }));
      setData({ ...data, remark, published: r.data.published, published_at: r.data.published[data.term] });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const kpi = (label, value, sub, color) => (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderTop: `4px solid ${color}`, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: "#94a3b8", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 5, fontSize: 19, fontWeight: 800, color: "#1e293b" }}>{value}</div>
      <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2 }}>{sub}</div>
    </div>
  );

  const sheetRemark = data?.remark || (data?.letter_grade ? REMARKS[data.letter_grade] : null);

  return (
    <Dialog open={!!studentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="report-card-modal">
        {!data ? (
          <div className="py-16 grid place-items-center"><div className="w-8 h-8 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" /></div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="flex bg-slate-100 rounded-full p-1" data-testid="report-card-terms">
                {data.terms.map((t) => (
                  <button
                    key={t}
                    data-testid={`report-card-term-${t.replace(/\s/g, "")}`}
                    onClick={() => t !== term && load(t)}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors duration-200 ${t === term ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <span
                data-testid="report-card-status"
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${data.published_at ? "bg-status-green/10 text-status-green" : "bg-status-amber/20 text-amber-700"}`}
              >
                {data.published_at ? `${term} · ${t("reportCard.publishedToParents", { defaultValue: "Published to parents" })}` : `${term} · ${t("reportCard.draft", { defaultValue: "Draft" })}`}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {user?.role === "school_admin" && (
                <Button variant="outline" data-testid="report-card-publish-btn" onClick={publish} disabled={busy === "publish"} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
                  <PaperPlaneTilt size={15} className="mr-1.5" /> {busy === "publish" ? t("reportCard.publishing", { defaultValue: "Publishing…" }) : t("reportCard.publishToParent", { defaultValue: "Publish to Parent Portal" })}
                </Button>
              )}
              <Button data-testid="report-card-download-btn" onClick={download} disabled={busy === "pdf" || data.subjects.length === 0} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                <DownloadSimple size={15} className="mr-1.5" /> {busy === "pdf" ? t("reportCard.preparing", { defaultValue: "Preparing…" }) : t("reportCard.downloadPdf", { defaultValue: "Download as PDF" })}
              </Button>
            </div>

            {data.letter_grade && (user?.role === "school_admin" || user?.role === "teacher") && (
              <div className="mb-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="remark-editor">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-extrabold tracking-widest uppercase text-slate-500">{t("reportCard.teacherRemarks", { defaultValue: "Teacher remarks" })} {!data.remark && <span className="text-slate-400 normal-case font-semibold">({t("reportCard.autoGenerated", { defaultValue: "auto-generated — edit to override" })})</span>}</label>
                  {remarkDirty && (
                    <button data-testid="remark-save-btn" onClick={saveRemark} disabled={busy === "remark"} className="text-xs font-bold text-brand-coralDark hover:underline inline-flex items-center gap-1">
                      <Check size={13} /> {busy === "remark" ? t("reportCard.saving", { defaultValue: "Saving…" }) : t("reportCard.saveRemark", { defaultValue: "Save remark" })}
                    </button>
                  )}
                </div>
                <textarea
                  data-testid="remark-input"
                  rows={2}
                  value={remark}
                  placeholder={REMARKS[data.letter_grade]}
                  onChange={(e) => { setRemark(e.target.value); setRemarkDirty(true); }}
                  className="w-full text-sm text-slate-600 italic bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-brand-coral resize-y"
                />
              </div>
            )}

            {(user?.role === "parent" || user?.role === "student") && sheetRemark && (
              <div className="mb-4 bg-white border border-slate-200 rounded-2xl p-4" data-testid="remark-readonly">
                <p className="text-xs font-extrabold tracking-widest uppercase text-slate-500 mb-1.5">{t("reportCard.teacherRemarks", { defaultValue: "Teacher remarks" })}</p>
                <p className="text-sm text-slate-600 italic">{sheetRemark}</p>
              </div>
            )}

            <div ref={sheetRef} data-testid="report-card-sheet" style={{ maxWidth: 800, margin: "0 auto", background: "#f8fafc", padding: 20, fontFamily: FONT, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: 4 }}>
              <div style={{ background: "#0f172a", color: "#cbd5e1", fontSize: 11, fontWeight: 800, letterSpacing: "0.28em", padding: "12px 20px", display: "flex", justifyContent: "space-between" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ background: "#ffffff", borderRadius: 6, padding: "3px 5px", display: "inline-flex" }}>
                    <img src="/edujac-mark.png" alt="EDUJAC" style={{ height: 14, width: 14 }} />
                  </span>
                  EDUJAC ERP
                </span>
                <span style={{ letterSpacing: "0.08em", color: "#64748b" }}>DOC-ID: {data.roll_no || data.student_id.slice(0, 8).toUpperCase()} · {data.term.toUpperCase()}</span>
              </div>
              <div style={{ background: "linear-gradient(120deg, #1e3a8a 0%, #1d4ed8 55%, #3b82f6 100%)", color: "#fff", padding: "28px 26px 24px" }}>
                <div style={{ fontSize: 23, fontWeight: 800 }}>{data.school_name}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: "#bfdbfe", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  {t("reportCard.officialTranscript", { defaultValue: "Official Student Performance Transcript" })} · {data.term} · {t("reportCard.issued", { defaultValue: "Issued" })} {data.issued_on}
                </div>
              </div>

              <div style={{ padding: "20px 22px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "#e2e8f0", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                  {[
                    [t("reportCard.studentName", { defaultValue: "Student Name" }), data.student_name],
                    [t("reportCard.rollNo", { defaultValue: "Roll No" }), data.roll_no || "—"],
                    [t("reportCard.class", { defaultValue: "Class" }), `${data.class_name}${data.grade_label ? ` · ${data.grade_label}` : ""}`],
                    [t("reportCard.homeroomLead", { defaultValue: "Homeroom Lead" }), data.homeroom],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: "#fff", padding: "13px 16px" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", color: "#94a3b8", textTransform: "uppercase" }}>{k}</div>
                      <div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 16 }}>
                  {kpi(t("reportCard.aggregateScore", { defaultValue: "Aggregate Score" }), data.total_max ? `${data.total_obtained}/${data.total_max}` : "—", t("reportCard.acrossGradedExams", { defaultValue: "across graded exams" }), "#3b82f6")}
                  {kpi(t("reportCard.percentage", { defaultValue: "Percentage" }), data.percentage != null ? `${data.percentage}%` : "—", data.letter_grade ? `${t("reportCard.grade", { defaultValue: "grade" })} ${data.letter_grade}` : t("reportCard.noExamsYet", { defaultValue: "no exams yet" }), "#22c55e")}
                  {kpi(t("reportCard.attendance", { defaultValue: "Attendance" }), data.attendance_pct_30d != null ? `${data.attendance_pct_30d}%` : "—", t("reportCard.last30Days", { defaultValue: "last 30 days" }), "#f59e0b")}
                  {kpi(t("reportCard.examsGraded", { defaultValue: "Exams Graded" }), data.exams_graded, `${t("reportCard.includedIn", { defaultValue: "included in" })} ${data.term}`, "#8b5cf6")}
                </div>

                <div style={{ margin: "22px 0 10px", fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: "#475569" }}>{t("reportCard.academicPerformance", { defaultValue: "Academic Performance" })} · {data.term}</div>
                {data.subjects.length === 0 ? (
                  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, textAlign: "center", fontSize: 13, color: "#94a3b8" }} data-testid="report-card-empty">
                    {t("reportCard.noGradedExams", { term: data.term, defaultValue: `No graded exams in ${data.term} for this class yet — enter exam marks first.` })}
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }} data-testid="report-card-table">
                    <thead>
                      <tr>
                        {[t("reportCard.subject", { defaultValue: "Subject" }), t("reportCard.exams", { defaultValue: "Exams" }), t("reportCard.obtained", { defaultValue: "Obtained" }), t("reportCard.outOf", { defaultValue: "Out of" }), "%", t("reportCard.gradeTitle", { defaultValue: "Grade" })].map((h, i) => (
                          <th key={h} style={{ background: "#0f172a", color: "#e2e8f0", fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", textAlign: i === 0 ? "left" : "center", padding: "10px 14px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.subjects.map((r, i) => (
                        <tr key={r.subject} data-testid={`report-card-row-${r.subject}`}>
                          <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>{r.subject}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, textAlign: "center", borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>{r.exams}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, textAlign: "center", borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>{r.obtained}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, textAlign: "center", borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>{r.max}</td>
                          <td style={{ padding: "10px 14px", fontSize: 13, textAlign: "center", fontWeight: 700, borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>{r.pct}%</td>
                          <td style={{ padding: "10px 14px", textAlign: "center", borderTop: "1px solid #eef2f7", background: i % 2 ? "#f8fafc" : "#fff" }}>
                            <span style={{ display: "inline-block", minWidth: 38, padding: "3px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, ...(PILL[r.grade] || PILL.C) }}>{r.grade}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {sheetRemark && (
                  <>
                    <div style={{ margin: "22px 0 10px", fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: "#475569" }}>{t("reportCard.classTeacherRemarks", { defaultValue: "Class Teacher's Remarks" })}</div>
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderLeft: "4px solid #3b82f6", borderRadius: 10, padding: "15px 18px", fontSize: 13, lineHeight: 1.65, color: "#475569", fontStyle: "italic" }} data-testid="report-card-remarks">
                      {sheetRemark}
                    </div>
                  </>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginTop: 32 }}>
                  {[[t("reportCard.classTeacher", { defaultValue: "Class Teacher" }), data.homeroom], [t("reportCard.principal", { defaultValue: "Principal" }), data.school_name], [t("reportCard.parentGuardian", { defaultValue: "Parent / Guardian" }), t("reportCard.verificationSignoff", { defaultValue: "Verification sign-off" })]].map(([who, role]) => (
                    <div key={who} style={{ textAlign: "center" }}>
                      <div style={{ borderBottom: "1.5px solid #94a3b8", height: 32, marginBottom: 8 }} />
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#475569" }}>{who}</div>
                      <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 3 }}>{role}</div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 24, paddingTop: 13, borderTop: "1px dashed #e2e8f0", display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#94a3b8" }}>
                  <span>{t("reportCard.generatedVia", { defaultValue: "Generated via EDUJAC ERP" })} · {data.school_name}</span>
                  <span>{data.term} · {t("reportCard.issued", { defaultValue: "Issued" })}: {data.issued_on}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
